// O2D.P-PRD-018 §TR4
// Canonical tiptap extension list shared between the view-mode `generateHTML`
// renderer (TR3) and the edit-mode `Editor` mount (TR4). Both paths MUST use the
// SAME list so the DOM output is identical (whiteboard idiom, see
// `packages/whiteboard/src/features/textbox/tiptap-extensions.ts:178-194`).
// One exception since 2026-09-26: in edit mode an image renders through
// tiptap's resize NodeView (a container with the `img` and its corner handles),
// in view mode as a plain `img`. The `img` attributes are the same in both
// except `width`/`height`: in edit mode the NodeView applies them as an inline
// size instead of HTML attributes.
//
// v1 extension set (Operator-Fixed):
//   - StarterKit — PLAIN, `undoRedo` NOT disabled (DD3). tiptap's built-in
//     undo/redo stays ENABLED for this standalone package (unlike whiteboard,
//     which sets `undoRedo: false` because `@o2d/history` owns history there).
//     StarterKit 3.27.x already bundles Underline, bold/italic/strike and both
//     bullet + ordered lists.
//   - TextStyle, Color, TextAlign — the inline-style / alignment marks the repo
//     already uses (not bundled by StarterKit). FontFamily was part of this set
//     until 0.3.0, see "Removed" below.
//   - TaskList + TaskItem — the third list type (checkbox/task), inkl. `checked`
//     toggle. Already in the repo (no new dependency).
//   - Table / TableRow / TableHeader / TableCell — the only NEW dependencies (TR7).
//
// Deliberately EXCLUDED:
//   - NO separate `Underline` — StarterKit 3.27.x bundles it; re-registering it
//     triggers a loud `Duplicate extension names underline` warning (TR4).
//   - NO raw-HTML-passthrough extension — the view-mode output must stay inert so
//     an untrusted document cannot inject executable script (TR3 XSS-inertness).
//
// Added after v1:
//   - Image (Anke, 2026-07-27) — pasted/inserted images via the element's
//     `imageUploadHandler` seam (mirrors @o2d/whiteboard). `<img src>` cannot run
//     script; the element still guards the src scheme on insertion. Since
//     2026-09-26 as `ResizableImage`: corner handles scale a selected image
//     (see the configuration in `createRichTextExtensions`).
//   - Block ids (0.2.0) — `UniqueID` puts an `id` attribute (lowercase UUID v7,
//     the id format open2done uses everywhere) on every block type listed in
//     `BLOCK_ID_TYPES`. open2done addresses blocks by this id in its edit
//     commands. A pasted block always gets a new id; existing ids in a loaded
//     document are kept. Opt out with `blockIds: false`.
//   - Nested checklists (0.2.0) — `TaskItem` with `nested: true`, so a checklist
//     item may contain a further checklist (content `paragraph block*`). Needed
//     to represent indented checklist items converted from Quill. Opt out with
//     `nestedTaskItems: false`.
//   The whiteboard textbox opts out of both, so its stored JSON and its own Tab
//   handling stay unchanged.
//   - ArrowInputRules — typing `->` (any number of dashes) or `>>` inserts `→ `,
//     `<->` (any number of dashes) inserts `↔ `. Always on.
//   - ClickReleasesNodeSelection (2026-09-21) — a click into text ends a node
//     selection, so typing no longer replaces a selected image or horizontal
//     rule. Always on: it is a data-loss fix, not a preference.
//
// Removed:
//   - FontFamily (0.3.0) — an editor never sets a font family; the font comes
//     from the surrounding application (open2done renders in "D-DIN"). The
//     extension had no toolbar control: its only effect was to absorb a
//     `font-family` from pasted Word/Docs HTML into a `textStyle` attribute,
//     which then rendered in a font different from the rest of the app. Without
//     it the attribute is not part of the schema, so a pasted font is dropped
//     while the HTML is parsed. Font STYLES are untouched — bold, italic,
//     underline and strike are separate marks. Note for hosts: a `fontFamily`
//     stored by an earlier version is discarded silently on the next save
//     (the schema keeps only known attributes), and font SIZE is unaffected —
//     a Word paste still brings an absolute `18pt` into `fontSize`.

import type { AnyExtension } from "@tiptap/core";
import { Extension, InputRule } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { TextAlign } from "@tiptap/extension-text-align";
import { Superscript } from "@tiptap/extension-superscript";
import { Subscript } from "@tiptap/extension-subscript";
import { Highlight } from "@tiptap/extension-highlight";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { UniqueID } from "@tiptap/extension-unique-id";
import { v7 as uuidv7 } from "uuid";
import { ClickReleasesNodeSelection } from "./click-releases-node-selection.js";
import { ResizableImage } from "./resizable-image.js";

/**
 * Block node types that carry an `id` attribute when block ids are enabled.
 * Table cells, table headers, hard breaks and text nodes deliberately carry none.
 * Names of node types that a caller has switched off (e.g. `image`, `table`) are
 * harmless: UniqueID only adds the attribute to node types that exist.
 */
export const BLOCK_ID_TYPES: readonly string[] = Object.freeze([
  "paragraph",
  "heading",
  "blockquote",
  "codeBlock",
  "image",
  "horizontalRule",
  "bulletList",
  "orderedList",
  "taskList",
  "listItem",
  "taskItem",
  "table",
  "tableRow",
]);

/**
 * Block id extension: attribute `id`, lowercase UUID v7. The generator is the
 * one `generateUniqueIds()` from `@tiptap/extension-unique-id` also reads, so
 * server-side id assignment yields the same format.
 */
function buildUniqueId() {
  return UniqueID.configure({
    attributeName: "id",
    types: [...BLOCK_ID_TYPES],
    generateID: () => uuidv7(),
  });
}

// Per-block horizontal alignment (Word-like): stored as a `text-align` attribute
// on each block node. Configured for the block types StarterKit provides so both
// edit- and view-mode agree on the rendered DOM.
//
// `defaultAlignment` is left at the extension's own default (`'left'`) here —
// unlike the whiteboard's copy of this config, which needs `null` (see
// `RichTextExtensionOptions.textAlignDefault` below; the whiteboard passes
// `null` through it to keep its own wrapper-inherits-default behavior).
function buildTextAlign(defaultAlignment: string | null | undefined) {
  return TextAlign.configure({
    types: ["heading", "paragraph"],
    ...(defaultAlignment === undefined ? {} : { defaultAlignment }),
  });
}

// Custom FontSize mark: piggy-backs on TextStyle (the canonical inline-style
// carrier) by adding a `fontSize` global attribute. Exported so consumers that
// need the SAME mark outside of `createRichTextExtensions()` (e.g. the
// whiteboard's own `DeferredFormatsExt`-adjacent extras) don't have to keep a
// second, copy-pasted definition in sync by hand.
export const FontSize = Extension.create({
  name: "fontSize",
  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.fontSize?.replace(/['"]+/g, "") || null,
            renderHTML: (attributes: { fontSize?: string | null }) => {
              if (!attributes.fontSize) {
                return {};
              }
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
});

// Typing convenience (Anke, 2026-09-14): an arrow typed as characters
// auto-replaces with a real arrow glyph followed by a space the moment the
// closing ">" is typed — no separate confirmation keystroke needed, same as
// e.g. a smart-quotes input rule:
//   - "->", "-->", "--->", … (any number of dashes) and ">>" give the SAME
//     single arrow "→ " ("...soll die Kombination aus '->' und '>>' zur
//     Darstellung EINES Pfeiles führen" — her own wording; the trailing space
//     is a same-day follow-up: "bitte nach dem pfeil gleich noch ein
//     leerzeichen mit anhängen"; more dashes: 2026-10-10, "bitte mache das
//     auch mit '-->'", then "auch '--->' zu einem Pfeil").
//   - "<->", "<-->", … give the double arrow "↔ " (2026-10-10). "↔" rather
//     than the long "⟷": the app font D-DIN has neither glyph, so both come
//     from a system fallback font — "↔" is in practically every system font,
//     "⟷" only in symbol/maths fonts and rendered narrow or not at all.
// `$`-anchored (not `^`) so it fires anywhere inline, not just at a line
// start. At a paragraph start "---" still becomes a horizontal rule before
// the ">" arrives (StarterKit's own rule). Undoable like any input rule.
//
// Code: tiptap never runs input rules while the cursor is in a code block or
// right next to inline code. A match can still REACH BACK into inline code
// that ends just before the typed dashes (e.g. `i--` then "->") — the
// greedy "-+" would swallow the code's dashes. `arrowInputRule` therefore
// replaces only the part after the last inline-code character, and only if
// that part is still an arrow on its own.
//
// ORDER MATTERS: tiptap's input-rule plugin (`@tiptap/core`, `InputRule.ts`,
// `run()`) applies the FIRST rule in this array that matches; a handler that
// returns null counts as no match and the next rule is tried. "<->" also ends
// in "->", so the double-arrow rule must come before the single one —
// otherwise "<->" would give "<→ ".

/** True when the character at `pos` carries a code mark (inline code) — the
 *  same test tiptap's own `run()` applies to the nodes around the cursor. */
function isInlineCode(state: EditorState, pos: number): boolean {
  return state.doc.nodeAt(pos)?.marks.some((mark) => mark.type.spec.code) ?? false;
}

/**
 * Like tiptap's `textInputRule`, but never replaces characters that sit in
 * inline code before the match (see the comment above). `find` must be
 * `$`-anchored; it is re-tested on the part of the match outside the code.
 */
function arrowInputRule(find: RegExp, replace: string): InputRule {
  return new InputRule({
    find,
    handler: ({ state, range, match }) => {
      // Typing over a selection: range.to is the selection's END, so the range
      // also holds the selected text that the typed character replaces. Do
      // exactly what tiptap's textInputRule does — replace the whole range —
      // since the inline-code reasoning below only holds for a caret.
      if (!state.selection.empty) {
        state.tr.insertText(replace, range.from, range.to);
        return undefined;
      }
      // With a caret, range.from..range.to holds the matched characters already
      // in the document; the rest of match[0] is the character being typed.
      let start = range.from;
      for (let pos = range.to - 1; pos >= range.from; pos--) {
        if (isInlineCode(state, pos)) {
          start = pos + 1;
          break;
        }
      }
      if (start !== range.from) {
        const typed = match[0].slice(range.to - range.from);
        if (!find.test(state.doc.textBetween(start, range.to) + typed)) {
          return null;
        }
      }
      state.tr.insertText(replace, start, range.to);
      return undefined; // replaced (null above means: no match, try the next rule)
    },
  });
}

const ArrowInputRules = Extension.create({
  name: "arrowInputRules",
  addInputRules() {
    return [
      arrowInputRule(/<-+>$/, "↔ "),
      arrowInputRule(/-+>$/, "→ "),
      arrowInputRule(/>>$/, "→ "),
    ];
  },
});

/**
 * Options for {@link createRichTextExtensions}. Every flag defaults to this
 * package's own behavior (the `RICH_TEXT_EXTENSIONS` below, including block ids
 * and nested checklists since 0.2.0) — a consumer with different needs (the
 * whiteboard textbox editor, so far the only other caller) opts OUT of
 * individual pieces rather than the other way round, so this package's own
 * defaults never silently drift.
 */
export interface RichTextExtensionOptions {
  /** StarterKit's built-in undo/redo. Off when a host (the whiteboard, via
   *  `@o2d/history`) owns undo itself — double-recording every keystroke
   *  otherwise. Default `true`. */
  undoRedo?: boolean;
  /** Passed through to StarterKit's `link` sub-extension config, e.g. to turn
   *  off click-to-navigate. Default: StarterKit's own default. */
  link?: Record<string, unknown>;
  /** StarterKit's `trailingNode` (auto-appended empty paragraph after a
   *  list/heading/etc. at doc end). Off for a tight, non-document shape like a
   *  textbox. Default `true`. */
  trailingNode?: boolean;
  /** `TextAlign`'s `defaultAlignment`. `undefined` (default) leaves the
   *  extension's own default (`'left'`); pass `null` for "untouched blocks
   *  inherit an outer default" semantics (the whiteboard's textbox wrapper). */
  textAlignDefault?: string | null;
  /** Real `Superscript`/`Subscript` marks. Off when a host already represents
   *  super/subscript another way (the whiteboard's `verticalScript` attribute
   *  on `textStyle`) — keeping both would give two independent, unsynced
   *  ways to express the same thing. Default `true`. */
  superscriptSubscript?: boolean;
  /** Multicolor `Highlight`. Default `true`. */
  highlight?: boolean;
  /** `Image` (paste/insert, `allowBase64` fallback). Default `true`. */
  image?: boolean;
  /** `Table`/`TableRow`/`TableHeader`/`TableCell`. Default `true`; the
   *  whiteboard excludes tables (not a fit for a free-floating textbox). */
  tables?: boolean;
  /** Block ids: an `id` attribute (lowercase UUID v7) on every type in
   *  {@link BLOCK_ID_TYPES}. Off for hosts whose stored JSON must not gain ids
   *  (the whiteboard textbox). Default `true`. */
  blockIds?: boolean;
  /** Checklist items may contain nested checklists (`paragraph block*`) and
   *  `Tab` indents an item. Off for hosts that handle `Tab` themselves (the
   *  whiteboard textbox). Default `true`. */
  nestedTaskItems?: boolean;
  /** Extensions appended after the ones above, for host-specific marks that
   *  have no equivalent here (e.g. the whiteboard's `DeferredFormatsExt`). */
  extras?: AnyExtension[];
}

/**
 * Builds the shared tiptap extension list — passed to BOTH the view-mode
 * `generateHTML(...)` call (TR3) and the edit-mode `new Editor(...)` mount
 * (TR4) so edit and view render byte-for-byte identically (images excepted:
 * edit mode wraps them in the resize NodeView, see the header). See
 * {@link RichTextExtensionOptions} for what a caller can opt out of; called
 * with no arguments this reproduces exactly the `RICH_TEXT_EXTENSIONS` list
 * below (block ids and nested checklists included).
 */
/**
 * The option keys this factory accepts, coupled to {@link RichTextExtensionOptions}:
 * `satisfies` makes BOTH a missing and a stray key a compile error, so this list
 * cannot drift out of step with the type. A hand-maintained list would be worse
 * than no check at all — forget to add a new option here and the factory rejects
 * a CORRECT key as a typo.
 */
const KNOWN_OPTION_KEYS = {
  undoRedo: true,
  link: true,
  trailingNode: true,
  textAlignDefault: true,
  superscriptSubscript: true,
  highlight: true,
  image: true,
  tables: true,
  blockIds: true,
  nestedTaskItems: true,
  extras: true,
} as const satisfies Record<keyof RichTextExtensionOptions, true>;

export function createRichTextExtensions(
  options: RichTextExtensionOptions = {},
): AnyExtension[] {
  // Reject an unknown key instead of ignoring it: a silently ignored key looks
  // exactly like a working opt-out, which is how a typo survives review. Only
  // the object's own top-level keys are inspected — nested option objects like
  // `link` carry StarterKit's keys, not this factory's. TypeScript already flags
  // an unknown key in an object LITERAL (measured); this check covers the paths
  // it does not see: a pre-built options variable and a cast.
  const unknownKeys = Object.keys(options).filter(
    (key) => !(key in KNOWN_OPTION_KEYS),
  );
  if (unknownKeys.length > 0) {
    throw new Error(
      `createRichTextExtensions: unknown option key(s) ${unknownKeys.join(", ")}. ` +
        `Allowed: ${Object.keys(KNOWN_OPTION_KEYS).sort().join(", ")} ` +
        `(see RichTextExtensionOptions in @o2d/rich-text-editor/extensions).`,
    );
  }

  const {
    undoRedo = true,
    link,
    trailingNode = true,
    textAlignDefault,
    superscriptSubscript = true,
    highlight = true,
    image = true,
    tables = true,
    blockIds = true,
    nestedTaskItems = true,
    extras = [],
  } = options;

  return [
    StarterKit.configure({
      // StarterKit's own types accept `false | Partial<Options> | undefined`
      // for these two — not a plain `true` — so "on" is expressed by omitting
      // the key (`undefined`) rather than passing `true` through.
      undoRedo: undoRedo ? undefined : false,
      trailingNode: trailingNode ? undefined : false,
      ...(link ? { link } : {}),
    }),
    TextStyle,
    Color,
    FontSize,
    ...(superscriptSubscript ? [Superscript, Subscript] : []),
    // multicolor: true → each highlight run carries its own color (needed for
    // the swatch palette; without it Highlight is a single on/off mark).
    ...(highlight ? [Highlight.configure({ multicolor: true })] : []),
    // Image (Anke, 2026-07-27): pasted/inserted images. `allowBase64` lets the
    // inline data-URL fallback render when NO upload handler is injected; with
    // a handler the node carries the uploaded asset URL instead (see the
    // element's `imageUploadHandler` seam — mirrors @o2d/whiteboard
    // O2D.W-PRD-042). The `<img src>` output stays inert (no script
    // execution); the element guards the src scheme on insertion.
    //
    // Scaling (Anke, 2026-09-26): corner handles resize a selected image;
    // the aspect ratio is always kept. The new size is stored as numeric
    // `width`/`height` attributes; `ResizableImage` adds the inline width
    // style that makes it visible in view mode and on undo.
    // Deliberately no `minHeight`: tiptap then applies no minimum height at
    // all (its default of 8 is replaced by `undefined`). With a fixed aspect
    // ratio a minimum height would enlarge small icons and keep wide banners
    // from getting narrower.
    // This package ships NO CSS for the handles — open2done's CSS lives only
    // in its own stylesheets, here `whiteboard_rte.css` (see ADR-013,
    // addendum 2026-09-26). Without host CSS the handles have no size and
    // cannot be grabbed. A host needs at least:
    //   - `[data-resize-handle]` — size, and visible on
    //     `[data-resize-container].ProseMirror-selectednode`;
    //   - `[data-resize-wrapper] > img { height: auto !important }` — the
    //     NodeView sets an inline height, which distorts the image once
    //     `max-width: 100%` narrows it;
    //   - `[data-resize-container] { width: fit-content }` — the NodeView sets
    //     `display: flex`, so the container would span the full line and a
    //     click next to the image would select it.
    // The selection class sits on the container, not on the `img`; this
    // package's former `img.ProseMirror-selectednode` outline could no longer
    // match and was removed together with its theme variable
    // `--o2d-rte-image-selected-outline` (Anke, review 0025/01).
    // Known tiptap 3.30.2 gap: a touch drag listens for `touchstart` but not
    // `touchend`, so a resize by touch is not committed.
    ...(image
      ? [
          ResizableImage.configure({
            allowBase64: true,
            resize: {
              enabled: true,
              directions: ["top-left", "top-right", "bottom-left", "bottom-right"],
              alwaysPreserveAspectRatio: true,
              minWidth: 40,
            },
          }),
        ]
      : []),
    buildTextAlign(textAlignDefault),
    TaskList,
    TaskItem.configure({ nested: nestedTaskItems }),
    ...(tables ? [Table, TableRow, TableHeader, TableCell] : []),
    ...(blockIds ? [buildUniqueId()] : []),
    ArrowInputRules,
    ClickReleasesNodeSelection,
    ...extras,
  ];
}

/**
 * Shared tiptap extension list for `<o2d-rich-text-editor>` — this package's
 * own defaults, i.e. `createRichTextExtensions()` with no overrides. Kept
 * as a plain constant so existing consumers (the element itself, the demo
 * app) don't need to change.
 */
export const RICH_TEXT_EXTENSIONS = createRichTextExtensions();
